import axios from 'axios';
import { toastError } from '../utils/toast';
import { 
  Company, 
  User, 
  CreateCompanyDto, 
  UpdateCompanyDto, 
  CreateUserDto, 
  UpdateUserDto,
  Order,
  CreateOrderDto,
  UpdateOrderDto,
  OrderStatus,
  Product,
  CreateProductDto,
  UpdateProductDto,
  Sale,
  CreateSaleDto,
  CreateSaleFromOrderDto,
  UpdateSaleDto,
  Currency,
  CreateCurrencyDto,
  UpdateCurrencyDto,
  Customer,
  CreateCustomerDto,
  UpdateCustomerDto,
  Subscription,
  CreateSubscriptionDto,
  UpdateSubscriptionDto,
  ProcessPaymentDto,
  TimeSeriesPoint,
  ReportingRangeParams,
  Location,
  CreateLocationDto,
  UpdateLocationDto,
  Loan,
  CreateLoanDto,
  UpdateLoanDto,
  RecordLoanPaymentDto,
} from '../types';
import { loadAuthToken, removeAuthToken } from '../utils/sessionStorage';
const isProd = !window.location.href.includes('localhost');
const API_PROD_URL = 'https://api.regixor.com';
const API_BASE_URL = isProd ? API_PROD_URL : 'http://localhost:3000';

// Function to get token from sessionStorage
const getAuthToken = (): string | null => {
  return loadAuthToken();
};

// Function to clear auth token
const clearAuthToken = (): void => {
  removeAuthToken();
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    // Remove quotes if they exist
    const cleanToken = token.startsWith('"') && token.endsWith('"') 
      ? token.slice(1, -1) 
      : token;
    
    config.headers.Authorization = `Bearer ${cleanToken}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAuthToken();
      window.location.href = '/login';
    }
    // Show a user-friendly error toast for API failures (except 401 which redirects)
    try {
      const serverMessage = error.response?.data?.message;
      const message = Array.isArray(serverMessage) ? serverMessage.join(', ') : (serverMessage || error.message || 'Request failed');
      // Avoid spamming on navigation aborts or cancellations
      if (error.code !== 'ERR_CANCELED') {
        toastError(message, 'Request Error');
      }
    } catch (_) {
      // no-op: never throw from interceptor
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  register: async (userData: any) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
};

// Companies API
export const companiesAPI = {
  getAll: async (): Promise<Company[]> => {
    const response = await api.get('/companies');
    return response.data;
  },
  getById: async (id: string): Promise<Company> => {
    const response = await api.get(`/companies/${id}`);
    return response.data;
  },
  create: async (data: CreateCompanyDto): Promise<Company> => {
    const response = await api.post('/companies', data);
    return response.data;
  },
  update: async (id: string, data: UpdateCompanyDto): Promise<Company> => {
    const response = await api.patch(`/companies/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/companies/${id}`);
  },
  restore: async (id: string): Promise<Company> => {
    const response = await api.patch(`/companies/${id}/restore`);
    return response.data;
  },
};

// Users API
export const usersAPI = {
  getAll: async (): Promise<User[]> => {
    const response = await api.get('/users');
    return response.data;
  },
  getById: async (id: string): Promise<User> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },
  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/users/me');
    return response.data;
  },
  getByCompany: async (companyId: string): Promise<User[]> => {
    const response = await api.get(`/users/company/${companyId}`);
    return response.data;
  },
  create: async (data: CreateUserDto): Promise<User> => {
    const response = await api.post('/users', data);
    return response.data;
  },
  update: async (id: string, data: UpdateUserDto): Promise<User> => {
    const response = await api.patch(`/users/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },
  restore: async (id: string): Promise<User> => {
    const response = await api.patch(`/users/${id}/restore`);
    return response.data;
  },
};

// Orders API
export const ordersAPI = {
  getAll: () => api.get<Order[]>('/orders').then(res => res.data),
  getById: (id: string) => api.get<Order>(`/orders/${id}`).then(res => res.data),
  create: (data: CreateOrderDto) => api.post<Order>('/orders', data).then(res => res.data),
  update: (id: string, data: UpdateOrderDto) => api.patch<Order>(`/orders/${id}`, data).then(res => res.data),
  delete: (id: string) => api.delete(`/orders/${id}`).then(res => res.data),
  getByCompany: (companyId: string) => api.get<Order[]>(`/orders/company/${companyId}`).then(res => res.data),
  getByStatus: (status: OrderStatus) => api.get<Order[]>(`/orders/status/${status}`).then(res => res.data),
  getByPaymentStatus: (paymentStatus: string) => api.get<Order[]>(`/orders/payment-status/${paymentStatus}`).then(res => res.data),
  getByCustomer: (customerId: string) => api.get<Order[]>(`/orders?customerId=${customerId}`).then(res => res.data),
  getPaymentStatus: (id: string) => api.get(`/orders/${id}/payment-status`).then(res => res.data),
  getPaymentHistory: (id: string) => api.get(`/orders/${id}/payment-history`).then(res => res.data),
  payOrder: (id: string, data: any) => api.post<Order>(`/orders/${id}/pay`, data).then(res => res.data),
  makePartialPayment: (id: string, data: { amount: number; notes?: string }) => api.post<Order>(`/orders/${id}/partial-payment`, data).then(res => res.data),
  completeOrder: (id: string, data: any) => api.patch<Order>(`/orders/${id}/complete-status`, data).then(res => res.data),
};

// Products API
export const productsAPI = {
  getAll: async (): Promise<Product[]> => {
    const response = await api.get('/products');
    return response.data;
  },
  getById: async (id: string): Promise<Product> => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },
  getByCompany: async (companyId: string): Promise<Product[]> => {
    const response = await api.get(`/products/company/${companyId}`);
    return response.data;
  },
  getLowStock: async (companyId: string): Promise<Product[]> => {
    const response = await api.get(`/products/company/${companyId}/low-stock`);
    return response.data;
  },
  create: async (data: CreateProductDto): Promise<Product> => {
    const response = await api.post('/products', data);
    return response.data;
  },
  update: async (id: string, data: UpdateProductDto): Promise<Product> => {
    const response = await api.patch(`/products/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/products/${id}`);
  },
  restore: async (id: string): Promise<Product> => {
    const response = await api.patch(`/products/${id}/restore`);
    return response.data;
  },
  updateInventory: async (id: string, quantity: number): Promise<Product> => {
    const response = await api.patch(`/products/${id}/inventory`, { quantity });
    return response.data;
  },
};

// Sales API
export const salesAPI = {
  getAll: async (): Promise<Sale[]> => {
    const response = await api.get('/sales');
    return response.data;
  },
  getById: async (id: string): Promise<Sale> => {
    const response = await api.get(`/sales/${id}`);
    return response.data;
  },
  getByCompany: async (companyId: string): Promise<Sale[]> => {
    const response = await api.get(`/sales/company/${companyId}`);
    return response.data;
  },
  getByUser: async (userId: string): Promise<Sale[]> => {
    const response = await api.get(`/sales/user/${userId}`);
    return response.data;
  },
  create: async (data: CreateSaleDto): Promise<Sale> => {
    const response = await api.post('/sales', data);
    return response.data;
  },
  update: async (id: string, data: UpdateSaleDto): Promise<Sale> => {
    const response = await api.patch(`/sales/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/sales/${id}`);
  },
  restore: async (id: string): Promise<Sale> => {
    const response = await api.patch(`/sales/${id}/restore`);
    return response.data;
  },
  createFromOrder: async (orderId: string, data: CreateSaleFromOrderDto): Promise<Sale> => {
    const response = await api.post(`/sales/from-order/${orderId}`, data);
    return response.data;
  },
  getByOrder: async (orderId: string): Promise<Sale[]> => {
    const response = await api.get(`/sales/order/${orderId}`);
    return response.data;
  },
  getBySubscription: async (subscriptionId: string, companyId: string): Promise<Sale[]> => {
    const response = await api.get(`/sales/subscription/${subscriptionId}/${companyId}`);
    return response.data;
  },
  getByCustomer: async (customerId: string): Promise<Sale[]> => {
    const response = await api.get(`/sales/customer/${customerId}`);
    return response.data;
  },
  recordSubscriptionPayment: async (
    subscriptionId: string,
    data: ProcessPaymentDto,
  ): Promise<Sale> => {
    const response = await api.post(`/sales/subscription/${subscriptionId}/payment`, data);
    return response.data;
  },
  refund: async (id: string, reason: string): Promise<Sale> => {
    const response = await api.post(`/sales/${id}/refund`, { reason });
    return response.data;
  },
};

// Reporting API
export const reportingAPI = {
  sales: async ({ from, to }: ReportingRangeParams): Promise<TimeSeriesPoint[]> => {
    const response = await api.get(`/reporting/sales`, { params: { from, to } });
    return response.data;
  },
  orders: async ({ from, to }: ReportingRangeParams): Promise<TimeSeriesPoint[]> => {
    const response = await api.get(`/reporting/orders`, { params: { from, to } });
    return response.data;
  },
  customers: async ({ from, to }: ReportingRangeParams): Promise<TimeSeriesPoint[]> => {
    const response = await api.get(`/reporting/customers`, { params: { from, to } });
    return response.data;
  },
};

// Currencies API
export const currenciesAPI = {
  getAll: async (): Promise<Currency[]> => {
    const response = await api.get('/currencies');
    return response.data;
  },
  getById: async (id: string): Promise<Currency> => {
    const response = await api.get(`/currencies/${id}`);
    return response.data;
  },
  getActive: async (): Promise<Currency[]> => {
    const response = await api.get('/currencies/active/list');
    return response.data;
  },
  getBaseCurrency: async (): Promise<Currency> => {
    const response = await api.get('/currencies/base/current');
    return response.data;
  },
  create: async (data: CreateCurrencyDto): Promise<Currency> => {
    const response = await api.post('/currencies', data);
    return response.data;
  },
  update: async (id: string, data: UpdateCurrencyDto): Promise<Currency> => {
    const response = await api.patch(`/currencies/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/currencies/${id}`);
  },
  restore: async (id: string): Promise<Currency> => {
    const response = await api.patch(`/currencies/${id}/restore`);
    return response.data;
  },
};

// Customers API
export const customersAPI = {
  getAll: async (): Promise<Customer[]> => {
    const response = await api.get('/customers');
    return response.data;
  },
  getById: async (id: string): Promise<Customer> => {
    const response = await api.get(`/customers/${id}`);
    return response.data;
  },
  getByCompany: async (companyId: string): Promise<Customer[]> => {
    const response = await api.get(`/customers/company/${companyId}`);
    return response.data;
  },
  getActiveByCompany: async (companyId: string): Promise<Customer[]> => {
    const response = await api.get(`/customers/company/${companyId}/active`);
    return response.data;
  },
  searchCustomers: async (companyId: string, searchTerm: string): Promise<Customer[]> => {
    const response = await api.get(`/customers/company/${companyId}/search?q=${encodeURIComponent(searchTerm)}`);
    return response.data;
  },
  create: async (data: CreateCustomerDto): Promise<Customer> => {
    const response = await api.post('/customers', data);
    return response.data;
  },
  update: async (id: string, data: UpdateCustomerDto): Promise<Customer> => {
    const response = await api.patch(`/customers/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/customers/${id}`);
  },
  restore: async (id: string): Promise<Customer> => {
    const response = await api.patch(`/customers/${id}/restore`);
    return response.data;
  },
  updateBalance: async (id: string, amount: number): Promise<Customer> => {
    const response = await api.patch(`/customers/${id}/balance`, { amount });
    return response.data;
  },
};

// Subscriptions API
export const subscriptionsAPI = {
  getAll: async (): Promise<Subscription[]> => {
    const response = await api.get('/subscriptions');
    return response.data;
  },
  getById: async (id: string): Promise<Subscription> => {
    const response = await api.get(`/subscriptions/${id}`);
    return response.data;
  },
  getByCompany: async (companyId: string): Promise<Subscription[]> => {
    const response = await api.get(`/subscriptions/company/${companyId}`);
    return response.data;
  },
  getActiveByCompany: async (companyId: string): Promise<Subscription | null> => {
    const response = await api.get(`/subscriptions/company/${companyId}/active`);
    return response.data;
  },
  create: async (data: CreateSubscriptionDto): Promise<Subscription> => {
    const response = await api.post('/subscriptions', data);
    return response.data;
  },
  update: async (id: string, data: UpdateSubscriptionDto): Promise<Subscription> => {
    const response = await api.patch(`/subscriptions/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/subscriptions/${id}`);
  },
  restore: async (id: string): Promise<Subscription> => {
    const response = await api.patch(`/subscriptions/${id}/restore`);
    return response.data;
  },
  processPayment: async (id: string, data: ProcessPaymentDto): Promise<Subscription> => {
    const response = await api.post(`/subscriptions/${id}/payment`, data);
    return response.data;
  },
  cancelSubscription: async (id: string, reason?: string): Promise<Subscription> => {
    const response = await api.patch(`/subscriptions/${id}/cancel`, { reason });
    return response.data;
  },
  suspendSubscription: async (id: string, reason?: string): Promise<Subscription> => {
    const response = await api.patch(`/subscriptions/${id}/suspend`, { reason });
    return response.data;
  },
  getExpiredSubscriptions: async (): Promise<Subscription[]> => {
    const response = await api.get('/subscriptions/expired/list');
    return response.data;
  },
  getPendingPayments: async (): Promise<Subscription[]> => {
    const response = await api.get('/subscriptions/pending-payments/list');
    return response.data;
  },
  calculateSubscriptionPrice: async (companyId: string, basePrice: number, pricePerLocation: number): Promise<{ totalAmount: number; activeLocations: number }> => {
    const response = await api.post('/subscriptions/calculate-price', {
      companyId,
      basePrice,
      pricePerLocation,
    });
    return response.data;
  },
};

export const locationsAPI = {
  getAll: async (): Promise<Location[]> => {
    const response = await api.get('/locations');
    return response.data;
  },
  getById: async (id: string): Promise<Location> => {
    const response = await api.get(`/locations/${id}`);
    return response.data;
  },
  create: async (data: CreateLocationDto): Promise<Location> => {
    const response = await api.post('/locations', data);
    return response.data;
  },
  update: async (id: string, data: UpdateLocationDto): Promise<Location> => {
    const response = await api.patch(`/locations/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/locations/${id}`);
  },
  assignEmployee: async (locationId: string, employeeId: string): Promise<User> => {
    const response = await api.post(`/locations/${locationId}/employees/${employeeId}`);
    return response.data;
  },
  unassignEmployee: async (employeeId: string): Promise<User> => {
    const response = await api.delete(`/locations/employees/${employeeId}`);
    return response.data;
  },
  getByCompany: async (companyId: string): Promise<Location[]> => {
    const response = await api.get(`/locations/company/${companyId}`);
    return response.data;
  },
};

export const loansAPI = {
  getByCompany: async (companyId: string): Promise<Loan[]> => {
    const response = await api.get(`/loans/company/${companyId}`);
    return response.data;
  },
  getById: async (companyId: string, loanId: string): Promise<Loan> => {
    const response = await api.get(`/loans/company/${companyId}/${loanId}`);
    return response.data;
  },
  getSummary: async (companyId: string, loanId: string): Promise<Loan> => {
    const response = await api.get(`/loans/company/${companyId}/${loanId}/summary`);
    return response.data;
  },
  create: async (loanData: CreateLoanDto): Promise<Loan> => {
    const response = await api.post('/loans', loanData);
    return response.data;
  },
  update: async (companyId: string, loanId: string, loanData: UpdateLoanDto): Promise<Loan> => {
    const response = await api.put(`/loans/company/${companyId}/${loanId}`, loanData);
    return response.data;
  },
  delete: async (companyId: string, loanId: string): Promise<void> => {
    await api.delete(`/loans/company/${companyId}/${loanId}`);
  },
  recordPayment: async (paymentData: RecordLoanPaymentDto): Promise<any> => {
    const response = await api.post('/loans/payments', paymentData);
    return response.data;
  },
  getByBorrower: async (borrowerId: string): Promise<Loan[]> => {
    const response = await api.get(`/loans/borrower/${borrowerId}`);
    return response.data;
  },
  getByLocation: async (locationId: string): Promise<Loan[]> => {
    const response = await api.get(`/loans/location/${locationId}`);
    return response.data;
  },
  getByOfficer: async (officerId: string): Promise<Loan[]> => {
    const response = await api.get(`/loans/officer/${officerId}`);
    return response.data;
  },
  getOverdue: async (companyId: string): Promise<Loan[]> => {
    const response = await api.get(`/loans/company/${companyId}/overdue`);
    return response.data;
  },
  getStatistics: async (companyId: string): Promise<{
    totalLoans: number;
    activeLoans: number;
    completedLoans: number;
    defaultedLoans: number;
    totalPrincipalAmount: number;
    totalCurrentBalance: number;
    totalInterestPaid: number;
    totalLateFees: number;
  }> => {
    const response = await api.get(`/loans/company/${companyId}/statistics`);
    return response.data;
  },
  getPaymentAmounts: async (companyId: string, loanId: string): Promise<{
    totalPayments: number;
    totalPrincipal: number;
    totalInterest: number;
    totalLateFees: number;
    paymentCount: number;
    lastPaymentDate: Date | null;
  }> => {
    const response = await api.get(`/loans/company/${companyId}/${loanId}/payment-amounts`);
    return response.data;
  },
  getPaymentHistory: async (companyId: string, loanId: string): Promise<Array<{
    id: string;
    amount: number;
    principalAmount: number;
    interestAmount: number;
    lateFeeAmount: number;
    paymentDate: Date;
    paymentMethod: string;
    notes: string | null;
    receiptNumber: string | null;
    overdueDays: number;
  }>> => {
    const response = await api.get(`/loans/company/${companyId}/${loanId}/payment-history`);
    return response.data;
  },
};

export default api;
